import { useEffect, useRef, useState, type RefObject } from "react";

interface UsePullToRefreshOptions {
  /** Pull distance (px) that must be reached to trigger a refresh. */
  threshold?: number;
  /** Maximum elastic pull distance (px). */
  maxPull?: number;
  /** Drag resistance factor (lower = stiffer). */
  resistance?: number;
  /** Disable the gesture entirely. */
  enabled?: boolean;
}

interface PullToRefreshState {
  /** Current elastic pull distance in px. */
  pullDistance: number;
  /** True while the refresh callback is running. */
  refreshing: boolean;
  /** True while the user is actively dragging. */
  pulling: boolean;
}

/**
 * Touch-only pull-to-refresh for a scrollable element.
 *
 * When the element is scrolled to the top and the user drags downward, an
 * elastic pull is tracked; releasing past `threshold` runs `onRefresh`. It's a
 * no-op on desktop (no touch events) so the behaviour is mobile-only.
 */
export function usePullToRefresh(
  scrollRef: RefObject<HTMLElement | null>,
  onRefresh: () => Promise<void> | void,
  options: UsePullToRefreshOptions = {}
): PullToRefreshState {
  const { threshold = 70, maxPull = 110, resistance = 0.5, enabled = true } =
    options;

  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [pulling, setPulling] = useState(false);

  // Mirror state in refs so the touch handlers stay stable (no re-binding).
  const startYRef = useRef<number | null>(null);
  const pullRef = useRef(0);
  const refreshingRef = useRef(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !enabled) return;

    const setPull = (value: number) => {
      pullRef.current = value;
      setPullDistance(value);
    };

    const onTouchStart = (e: TouchEvent) => {
      if (refreshingRef.current) return;
      if (el.scrollTop <= 0) {
        startYRef.current = e.touches[0].clientY;
      } else {
        startYRef.current = null;
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (startYRef.current === null || refreshingRef.current) return;

      const delta = e.touches[0].clientY - startYRef.current;

      // Cancel if the user scrolled down or moved up past the origin.
      if (delta <= 0 || el.scrollTop > 0) {
        if (pullRef.current !== 0) setPull(0);
        setPulling(false);
        return;
      }

      if (e.cancelable) e.preventDefault();
      setPulling(true);
      setPull(Math.min(delta * resistance, maxPull));
    };

    const onTouchEnd = async () => {
      if (startYRef.current === null) return;
      startYRef.current = null;
      setPulling(false);

      if (pullRef.current >= threshold) {
        refreshingRef.current = true;
        setRefreshing(true);
        setPull(threshold);
        try {
          await onRefresh();
        } finally {
          refreshingRef.current = false;
          setRefreshing(false);
          setPull(0);
        }
      } else {
        setPull(0);
      }
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd);
    el.addEventListener("touchcancel", onTouchEnd);

    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [scrollRef, onRefresh, threshold, maxPull, resistance, enabled]);

  return { pullDistance, refreshing, pulling };
}
